import React, { useState, useEffect, useCallback } from 'react';
import { Button, Skeleton, FormModal } from '../../components/ui';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/adminApi';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Search,
  People,
  Email,
  Person,
  Phone,
  CalendarMonth,
  Warning,
  CheckCircle,
  Cancel,
  Visibility,
  CreditCard,
  LocalOffer,
  Restaurant,
  PersonOff
} from '../../mui-icons';
import './UsersPage.css';

const UsersPage = () => {
  const navigate = useNavigate();
  
  // States
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search })
      };

      const response = await adminAPI.getUsers(params);
      
      if (response.status === 'success') {
        setUsers(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Handle search
  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Open detail modal
  const openDetailModal = async (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
    setDetailLoading(true);

    try {
      const userResponse = await adminAPI.getUser(user.idusers);
      
      // A API retorna { data: { user: {...}, subscription: {...}, coupon: {...} } }
      const responseData = userResponse.data;
      
      setUserDetail({
        // Dados do usuário
        ...responseData.user,
        // Assinatura vem junto na mesma resposta
        subscription: responseData.subscription || null,
        // Cupom vem junto na mesma resposta
        coupon: responseData.coupon || null,
        // Comissão
        commission: responseData.commission || null
      });
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format datetime
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get subscription status badge
  const getSubscriptionBadge = (user) => {
    if (user.has_active_subscription) {
      return <span className="status-badge status-active">Premium</span>;
    }
    return <span className="status-badge status-inactive">Free</span>;
  };

  // Render loading skeleton
  if (loading && users.length === 0) {
    return (
      <div className="users-page">
        <div className="page-header">
          <Skeleton width={200} height={32} borderRadius={8} />
        </div>
        <div className="admin-card pn-card pn-card--default">
          <Skeleton width={300} height={40} borderRadius={8} style={{ marginBottom: 20 }} />
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} width="100%" height={60} borderRadius={8} style={{ marginBottom: 12 }} />
          ))}
        </div>
      </div>
    );
  }

  // Sub-navigation items para o grupo Usuários
  const subNavItems = [
    { path: '/admin/users', label: 'Usuários', icon: <People sx={{ fontSize: 18 }} /> },
    { path: '/admin/users-temp', label: 'Cadastros Incompletos', icon: <PersonOff sx={{ fontSize: 18 }} /> }
  ];

  return (
    <div className="users-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <People sx={{ fontSize: 32 }} />
          <div>
            <h1>Usuários</h1>
            <p>{pagination.total} usuário(s) cadastrado(s)</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadUsers}>
              Tentar novamente
            </Button>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <People sx={{ fontSize: 64 }} className="empty-state-icon" />
            <h3 className="empty-state-title">Nenhum usuário encontrado</h3>
            <p className="empty-state-text">
              {search ? 'Tente uma busca diferente' : 'Ainda não há usuários cadastrados'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>ID</th>
                    <th style={{ width: '50px' }}></th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Cadastro</th>
                    <th>Cupom</th>
                    <th>Plano</th>
                    <th style={{ width: '80px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.idusers}>
                      <td>
                        <span className="user-id-cell">{user.idusers}</span>
                      </td>
                      <td>
                        <div className="user-avatar">
                          <Person />
                        </div>
                      </td>
                      <td>
                        <strong>{user.name || 'Sem nome'}</strong>
                      </td>
                      <td>{user.mail || user.email || '-'}</td>
                      <td>{user.tel || '-'}</td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        {user.coupon?.code ? (
                          <span className="coupon-badge">{user.coupon.code}</span>
                        ) : (
                          <span className="no-coupon">-</span>
                        )}
                      </td>
                      <td>{getSubscriptionBadge(user)}</td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            variant="secondary"
                            className="pn-btn--icon"
                            onClick={() => openDetailModal(user)}
                            title="Ver detalhes"
                          >
                            <Visibility sx={{ fontSize: 18 }} />
                          </Button>
                          <Button
                            variant="primary"
                            className="pn-btn--icon"
                            onClick={() => navigate(`/admin/users/${user.idusers}/meals`)}
                            title="Ver refeições"
                          >
                            <Restaurant sx={{ fontSize: 18 }} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>

                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Detalhes do Usuário"
        large
        footer={
          <>
            <Button
              variant="primary"
              onClick={() => {
                setShowDetailModal(false);
                navigate(`/admin/users/${selectedUser?.idusers}/meals`);
              }}
            >
              <Restaurant sx={{ fontSize: 18 }} /> Ver Refeições
            </Button>
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              Fechar
            </Button>
          </>
        }
      >
        {detailLoading ? (
          <div className="detail-loading">
            <Skeleton width="100%" height={24} borderRadius={4} style={{ marginBottom: 12 }} />
            <Skeleton width="100%" height={120} borderRadius={8} style={{ marginBottom: 16 }} />
            <Skeleton width="100%" height={80} borderRadius={8} />
          </div>
        ) : userDetail ? (
                <div className="user-detail">
                  {/* User Info */}
                  <div className="detail-section">
                    <h3>
                      <Person />
                      Informações Pessoais
                    </h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>ID</label>
                        <span>{userDetail.idusers}</span>
                      </div>
                      <div className="detail-item">
                        <label>Nome</label>
                        <span>{userDetail.name || 'Não informado'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Email</label>
                        <span>{userDetail.mail || userDetail.email || 'Não informado'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Telefone</label>
                        <span>{userDetail.tel || 'Não informado'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Data de Nascimento</label>
                        <span>{formatDate(userDetail.birth_date)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Gênero</label>
                        <span>
                          {userDetail.sex === 'masculino' ? 'Masculino' : 
                           userDetail.sex === 'feminino' ? 'Feminino' : 
                           userDetail.sex || 'Não informado'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>País</label>
                        <span>{userDetail.country || 'Não informado'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Estado</label>
                        <span>{userDetail.state || 'Não informado'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Tipo de Login</label>
                        <span className="login-type-badge">{userDetail.login_type || 'email'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Cadastro</label>
                        <span>{formatDateTime(userDetail.created_at)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Última atualização</label>
                        <span>{formatDateTime(userDetail.updated_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Info */}
                  <div className="detail-section">
                    <h3>
                      <CreditCard />
                      Assinatura
                    </h3>
                    {userDetail.subscription ? (
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Status</label>
                          <span className={`status-badge ${userDetail.subscription.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                            {userDetail.subscription.status === 'active' ? 'Ativa' : 
                             userDetail.subscription.status === 'trial' ? 'Trial' :
                             userDetail.subscription.status === 'cancelling' ? 'Cancelando' : 'Inativa'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <label>Provedor</label>
                          <span className="provider-badge">
                            {userDetail.subscription.provider === 'stripe' ? 'Stripe' : 
                             userDetail.subscription.provider === 'apple' ? 'Apple' : 
                             userDetail.subscription.provider || 'N/A'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <label>Início do Período</label>
                          <span>{formatDate(userDetail.subscription.current_period_start)}</span>
                        </div>
                        <div className="detail-item">
                          <label>Fim do Período</label>
                          <span>{formatDate(userDetail.subscription.current_period_end)}</span>
                        </div>
                        {userDetail.subscription.cancel_at_period_end && (
                          <div className="detail-item">
                            <label>Cancelamento</label>
                            <span className="cancel-warning">Cancela no fim do período</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="no-subscription">Usuário não possui assinatura ativa</p>
                    )}
                  </div>

                  {/* Coupon Info */}
                  {userDetail.coupon && (
                    <div className="detail-section">
                      <h3>
                        <LocalOffer />
                        Cupom Utilizado
                      </h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Código</label>
                          <span className="coupon-badge">{userDetail.coupon.code}</span>
                        </div>
                        <div className="detail-item">
                          <label>Influenciador</label>
                          <span>{userDetail.coupon.influencer?.name || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Comissão</label>
                          <span>{userDetail.coupon.commission_percent}% por {userDetail.coupon.commission_months} meses</span>
                        </div>
                        <div className="detail-item">
                          <label>Usado em</label>
                          <span>{formatDateTime(userDetail.coupon.used_at)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
        ) : (
          <p className="no-detail">Não foi possível carregar os detalhes</p>
        )}
      </FormModal>
    </div>
  );
};

export default UsersPage;


